import { useEffect, useState } from "react";

import { helpSearchAnalytics } from "@bokio/components/Help/HelpSearchAnalytics";
import { HelpWindowMode } from "@bokio/components/Help/helpSupportUtils";
import { useLoader } from "@bokio/hooks/useLoader/useLoader";
import * as m from "@bokio/mobile-web-shared/core/model/model";
import { useUmbracoApi } from "@bokio/shared/services/api/UmbracoApi";
import { trackError } from "@bokio/utils/t";

import { HelpContext } from "./HelpContext";

import type { FC, PropsWithChildren } from "react";

export const HelpContextProvider: FC<PropsWithChildren> = ({ children }) => {
	const [visible, setVisible] = useState<boolean>(false);
	const defaultViewHistory = [HelpWindowMode.Default];
	const [viewHistory, setViewHistory] = useState<HelpWindowMode[]>(defaultViewHistory);
	const onNavigate = (view: HelpWindowMode) => setViewHistory(prev => [...prev, view]);
	const onClearHistory = () => setViewHistory(defaultViewHistory);
	const { getHelp, getCachedHelpMetadata } = useUmbracoApi();

	const openHelpArticleLoader = useLoader({
		endpoint: (id: string) => getHelp(id),
		onSuccess: model => helpSearchAnalytics.trackHelpArticle(model.Url),
		onError: (error, params) => trackError(error, "app.HelpContextProvider", { parameters: params }),
	});

	const [searchString, setSearchString] = useState<string>("");
	const [areaPrefilled, setAreaPrefilled] = useState<m.Contracts.SupportFormArea>(m.Contracts.SupportFormArea.NotSet);
	const [modalMode, setModalMode] = useState<boolean>(false);
	const [messagePrefilled, setMessagePrefilled] = useState<string>("");
	const [referenceId, setReferenceId] = useState<string>("");
	const [screenshotElement, setScreenshotElement] = useState<HTMLDivElement | null>(null);

	const openHelpArticle = async (id: string) => {
		await openHelpArticleLoader.load(id);
	};

	const resetHelpArticle = () => openHelpArticleLoader.reset();

	const onGoBack = () => {
		setViewHistory(prev => {
			const newHistory = prev.slice(0, -1);
			return newHistory.length ? newHistory : defaultViewHistory;
		});
		resetHelpArticle();
	};

	useEffect(() => {
		if (!visible) {
			// eslint-disable-next-line react-hooks/set-state-in-effect -- SS 2025-10-13 This lint error should be fixed in the future. Existing usages were disabled just to simplify merging lint rules upgrade.
			setModalMode(false);
			setAreaPrefilled(m.Contracts.SupportFormArea.NotSet);
			setMessagePrefilled("");
			setReferenceId("");
		}
	}, [visible]);

	return (
		<HelpContext
			value={{
				visible,
				toggleVisible: () => setVisible(prev => !prev),
				show: () => setVisible(true),
				view: viewHistory[viewHistory.length - 1],
				onNavigate,
				onClearHistory,
				onGoBack,
				activeHelpArticle: openHelpArticleLoader.request.data,
				openHelpArticle,
				resetHelpArticle,
				openArticleIsLoading: openHelpArticleLoader.request.isLoading,
				openArticleIsError: openHelpArticleLoader.request.error,
				searchString,
				setSearchString,
				getCachedHelpMetadata,
				areaPrefilled,
				setAreaPrefilled,
				messagePrefilled,
				setMessagePrefilled,
				referenceId,
				setReferenceId,
				modalMode,
				setModalMode,
				screenshotElement,
				setScreenshotElement,
			}}
		>
			{children}
		</HelpContext>
	);
};
